// vue
import { useStore } from '@/store';

// models
import {
    GeoRecord,
    NewGeoCoords,
    TreeNode,
    GeoRecordTreeRole,
} from '@/vue3/api/models';

// hooks
import { useAxios } from '@/vue3/hooks/axiosHook';

export const useGeoRecordService = () => {
    const { apiUrl } = useStore().state;
    const axios = useAxios();

    // methods
    const getAllAsTree = async (courseSchemaId: number) => {
        const res = await axios.get(
            `${apiUrl}/geo-record/tree/course-schema/${courseSchemaId}`,
        );
        return res.data;
    };

    const getAllAsTreeByRole = async (
        GeoRecordTreeRole: GeoRecordTreeRole,
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record/tree/temp/course-schema`,
            GeoRecordTreeRole,
        );
        return res.data;
    };

    const getAllCustomLayersAsTree = async (
        GeoRecordTreeRole: GeoRecordTreeRole,
    ): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record/tree/custom/course-schema`,
            GeoRecordTreeRole,
        );
        return res.data;
    };

    const getAllGeoRecordSummary = async (courseSchemaId: number) => {
        const res = await axios.get(
            `${apiUrl}/geo-record/course-schema/summary/${courseSchemaId}`,
        );
        return res && res.data && res.data.length ? res.data : [];
    };

    const getGeoRecordAsTree = async (
        courseSchemaId: Number,
    ): Promise<TreeNode> => {
        const res = await axios.get(
            `${apiUrl}/geo-record/tree/course-schema/${courseSchemaId}`,
        );
        return res.data;
    };

    const getGeoRecord = async (geoRecordId: number): Promise<GeoRecord> => {
        const res = await axios.get(
            `${apiUrl}/geo-record/${geoRecordId}/geojson`,
        );
        return res.data;
    };

    const migrateGeoRecordImage = async () => {
        const res = await axios.put(`${apiUrl}/geo-record/image-migrate`, '');

        return res.data;
    };

    const createGeoRecord = async (geoRecord: any): Promise<GeoRecord> => {
        let fd = new FormData();
        let geoRecordImageDates: any[] | any[] = [];
        fd.append('json', JSON.stringify(geoRecord));
        if (geoRecord.images) {
            geoRecord.images.forEach((geoRecordImage, i) => {
                fd.append(`image${i}`, geoRecordImage.image);
                if (geoRecordImage.date) {
                    let dateString = '' + +geoRecordImage.date;
                    let shortenedDateString = dateString.slice(
                        0,
                        dateString.length - 3,
                    );
                    fd.append(`image-date${i}`, shortenedDateString);
                } else {
                    fd.append(
                        `image-date${i}`,
                        JSON.stringify(geoRecordImage.date),
                    );
                }
            });
        }

        const res = await axios.post(`${apiUrl}/geo-record/geojson`, fd, 10000);
        return res.data;
    };

    const createMultipleGeoRecords = async (geoRecordsFormData: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/geojson/multiple`,
            geoRecordsFormData,
            10000,
        );

        return res.data;
    };

    const updateGeoRecord = async (geoRecord: any, isModification: boolean) => {
        let fd = new FormData();
        let geoRecordImageDates: any[] | any[] = [];
        fd.append('json', JSON.stringify(geoRecord));
        fd.append('isModification', JSON.stringify(isModification));
        if (geoRecord.images) {
            geoRecord.images.forEach((geoRecordImage, i) => {
                fd.append(`image${i}`, geoRecordImage.image);
                if (geoRecordImage.date) {
                    let dateString = '' + +geoRecordImage.date;
                    let shortenedDateString = dateString.slice(
                        0,
                        dateString.length - 3,
                    );
                    fd.append(`image-date${i}`, shortenedDateString);
                } else {
                    fd.append(
                        `image-date${i}`,
                        JSON.stringify(geoRecordImage.date),
                    );
                }
            });
        }

        const res = await axios.put(`${apiUrl}/geo-record/geojson`, fd, 10000);
        return res;
    };

    const updateMultipleGeoRecords = async (geoRecordsFormData) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/geojson/update-multiple`,
            geoRecordsFormData,
        );
        return res.data;
    };

    const getMultipleGeoRecords = async (
        geoRecordIds: number[],
    ): Promise<GeoRecord[]> => {
        const res = await axios.post(
            `${apiUrl}/geo-record/geojson/get-multiple`,
            geoRecordIds,
            10000,
        );
        return res.data;
    };

    const smoothGeoRecord = async (
        geoRecordId: number,
        smoothValue: number,
    ): Promise<any> => {
        const res = await axios.put(
            `${apiUrl}/geo-record/smooth/${geoRecordId}/${smoothValue}`,
            10000,
        );
        return res;
    };

    const duplicateGeoRecord = async (newData: NewGeoCoords): Promise<any> => {
        const res = await axios.post(
            `${apiUrl}/geo-record/duplicate`,
            newData,
            10000,
        );
        return res;
    };

    const getGeoRecordArea = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/temp-postgis/geometry-area/${id}`,
            10000,
        );
        return res.data;
    };

    const getGeoRecordLength = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/temp-postgis/geometry-length/${id}`,
        );
        return res.data;
    };

    const getGeoRecordPerimeter = async (id: number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/temp-postgis/geometry-perimeter/${id}`,
        );
        return res.data;
    };

    const getSrid = async (courseSchemaId: Number): Promise<any> => {
        const res = await axios.get(
            `${apiUrl}/postgis/course/srid/${courseSchemaId}`,
        );
        return res.data.srid;
    };

    const deleteGeoRecord = async (id: number) => {
        const res = await axios.deleteId(
            `${apiUrl}/geo-record/${id}/geojson`,
            10000,
        );
        return res;
    };

    const createDeleteGeoRecordRequest = async (id: number) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/geojson/${id}/delete-request`,
            10000,
        );
        if (res.status == 201) {
            return true;
        } else {
            return false;
        }
    }

    const deleteMultipleGeoRecord = async (ids: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/geojson/multiple-delete`,
            ids,
            10000,
        );
        return res;
    };

    const createGeoRecordFilter = async (fd: any) => {
        const res = await axios.post(`${apiUrl}/geo-record-filter`, fd);
        return res.data;
    };

    const deleteGeoRecordFilter = async (id: any) => {
        const res = await axios.deleteId(`${apiUrl}/geo-record-filter/${id}`);
        return res;
    };

    const getGeoRecordFilter = async () => {
        const res = await axios.get(`${apiUrl}/geo-record-filter`);
        return res.data;
    };

    const getTags = async () => {
        const res = await axios.get(`${apiUrl}/geo-record-tags`);
        return res.data;
    };

    const createTags = async (Tags: any) => {
        const res = await axios.post(`${apiUrl}/geo-record-tags`, Tags);
        return res.data;
    };

    const deleteTag = async (id: number) => {
        const res = await axios.deleteId(`${apiUrl}/geo-record-tags/${id}`);
        return res.data;
    };

    const uploadDXF = async (formData: any, customFlag: 0 | 1 = 0) => {
        const res = await axios.post(
            `${apiUrl}/convert-dxf/${customFlag}`,
            formData,
        );
        if (res.status == 200) {
            return res.data;
        } else {
            return false;
        }
    };

    const getDXFFromGeoJSON = async (
        formData: any,
        epsgCodeDxf: number = 3857,
    ) => {
        const res = await axios.post(
            `${apiUrl}/download-dxf/${epsgCodeDxf}`,
            formData,
            undefined,
            { responseType: 'blob' },
            { 'Content-Type': 'multipart/form-data' },
        );
        if (res.status == 200) {
            return {
                file: res.data,
                successCount: res.headers['x-success-sount'],
                failCount: res.headers['x-failed-count'],
            };
        } else {
            return {
                file: false,
                successCount: res.headers['x-success-count'],
                failCount: res.headers['x-failed-count'],
            };
        }
    };

    const getAllGeoRecordLibraryEntry = async (): Promise<GeoRecord[]> => {
        const res = await axios.get(`${apiUrl}/geo-record-library`);
        if (res.status == 200) {
            return (res.data || []).map((x: any) => {
                return {
                    id: x.id,
                    key: x.key,
                    title: x.title,
                };
            });
        } else {
            return [];
        }
    };

    const getGeoRecordLibraryEntryByID = async (
        geoRecordId: number,
    ): Promise<GeoRecord> => {
        const res = await axios.get(
            `${apiUrl}/geo-record-library/${geoRecordId}`,
        );
        return res.data;
    };

    const createGeoRecordLibraryEntry = async (
        geoRecord: any,
    ): Promise<GeoRecord> => {
        let fd = new FormData();
        fd.append('json', JSON.stringify(geoRecord));

        const res = await axios.post(`${apiUrl}/geo-record-library`, fd, 10000);
        return res.data;
    };

    const updateGeoRecordLibraryEntry = async (
        geoRecord: any,
        isModification: boolean,
    ) => {
        let fd = new FormData();
        let geoRecordImageDates: any[] | any[] = [];
        fd.append('json', JSON.stringify(geoRecord));
        fd.append('isModification', JSON.stringify(isModification));
        const res = await axios.put(`${apiUrl}/geo-record-library`, fd, 10000);
        return res;
    };

    const deleteGeoRecordLibraryEntry = async (id: number) => {
        const res = await axios.deleteId(
            `${apiUrl}/geo-record-library/${id}`,
            10000,
        );
        return res;
    };

    const updateCadLayerTitle = async (layerId: number, newTitle: string) => {
        const res = await axios.put(
            `${apiUrl}/geo-record/${layerId}/title`,
            { newTitle },
            10000,
        );
        return res;
    };
    const handleDeleteRequest = async (payload: any) => {
        const res = await axios.post(
            `${apiUrl}/geo-record/delete-request`,
            payload,
            10000,
        );
        return res;
    }

    return {
        getAllAsTree,
        getAllGeoRecordSummary,
        getGeoRecordAsTree,
        getGeoRecord,
        createGeoRecord,
        updateGeoRecord,
        smoothGeoRecord,
        duplicateGeoRecord,
        getGeoRecordArea,
        getGeoRecordLength,
        getGeoRecordPerimeter,
        getAllAsTreeByRole,
        getAllCustomLayersAsTree,
        getSrid,
        deleteGeoRecord,
        createDeleteGeoRecordRequest,
        migrateGeoRecordImage,
        createGeoRecordFilter,
        getGeoRecordFilter,
        deleteGeoRecordFilter,
        getTags,
        createTags,
        updateMultipleGeoRecords,
        getMultipleGeoRecords,
        createMultipleGeoRecords,
        deleteMultipleGeoRecord,
        uploadDXF,
        getDXFFromGeoJSON,
        deleteTag,
        getAllGeoRecordLibraryEntry,
        getGeoRecordLibraryEntryByID,
        createGeoRecordLibraryEntry,
        updateGeoRecordLibraryEntry,
        deleteGeoRecordLibraryEntry,
        updateCadLayerTitle,
        handleDeleteRequest,
    };
};
